// theme.js — every colour and typeface the panel uses, named by role.
//
// Restyle the panel here and nowhere else. Components read a palette
// as `T` (app.jsx picks LIGHT or DARK and adds the Comfort `scale`),
// and they ask for a colour by what it is FOR, never by what it looks
// like. That is the point of the file: until 2026-10 one `accent`
// served lights-on, selections, in-flight spinners, live indicators,
// heating and decoration alike, so it meant nothing at a glance and
// could not be changed for one purpose without changing all of them.
//
// Roles
//   paper / card / cardSoft   page, raised surface, quiet fill on a surface
//   ink / inkSoft / mute      primary, secondary and tertiary text
//   hair / hairHi             dividers and outlines
//   lit / litSoft             a device that is ON (lights, utilities, screens)
//   litFill / litFillInk      a lit light key, and its text
//   litCell                   the level cell on a lit key
//   keyFill                   a key at rest
//   selected / selectedSoft   the chosen option in a group (mode, source, louvre)
//   selectedInk               text on a selected fill
//   busy / busySoft           an action still in flight (spinners, halos)
//   live                      something playing or running (eq bars, badges)
//   heat / cool               the climate mode a reading belongs to
//   attention                 something that needs the household to act
//   attentionFill / attentionInk  a whole card in that state (the power card
//                             in a price spike), and its text
//   pool                      the pool and its readings
//   primary / primaryInk      the one main action on a surface, and its text
//   decor                     colour used purely as ornament (no meaning)
//   onPhoto                   text and glyphs set over a photograph
//   heroScrimTop / Bottom     the darkening that keeps that text legible
//   cardShadow                a card's lift off the page (none in dark)
//   keyShadow / litShadow     a key's lift at rest, and its warm glow when lit
//   shadow / scrim            overlay elevation and the dimmer behind it
//
// The `*Soft` values are translucent washes of their role, for fills
// behind a coloured outline or label.

// Corner radii. Cards are the softest; keys and buttons a step tighter;
// `full` is for round controls and pills.
export const RADIUS = { key: 10, control: 14, card: 16, full: 999 };

export const FONT = {
  serif: '"Cormorant Garamond", "Lora", Georgia, serif',
  sans: '"Inter Tight", "Inter", -apple-system, sans-serif',
};

// Values: sampled from the house photographs (plaster, black-stained
// joinery, oak, sandstone, the linen throw, the rust cushions, the pool),
// then nudged where a material is used for text so it passes WCAG AA.
// Oak means "a light is on" and nothing else; rust means "needs you" and
// nothing else; whatever is selected takes black joinery. Approved on the
// redesign canvas, 2026-10-21.
//
// Three copies of the light page colour live where this module cannot
// reach — index-pwa.html (theme-color meta and body background) and
// public/manifest-pwa.webmanifest. Change them with `paper`.

export const LIGHT = {
  isDark:   false,
  paper:    '#F4F4F2',   // plaster
  card:     '#FFFFFF',
  cardSoft: '#EFEEEB',   // quiet button and track fill
  ink:      '#222120',   // black joinery
  inkSoft:  '#4B4946',
  mute:     '#6A6763',
  hair:     '#DCDAD6',
  hairHi:   '#C9C6C0',

  lit:          '#6B4F2F',                  // deep oak: text and glyphs of a lit thing
  litSoft:      'rgba(218,191,157,.35)',    // oak wash behind them
  litFill:      '#DABF9D',                  // house oak: a lit key
  litFillInk:   '#222120',
  litCell:      '#CDB18B',                  // the lit key's level cell
  keyFill:      '#FFFFFF',                  // a key at rest
  selected:     '#222120',
  selectedSoft: 'rgba(34,33,32,.07)',
  selectedInk:  '#F4F4F2',
  busy:         '#222120',
  busySoft:     'rgba(34,33,32,.07)',
  live:         '#222120',
  heat:         '#222120',
  cool:         '#605F79',   // linen throw
  attention:    '#A0603A',   // rust cushion
  attentionFill: '#A0603A',
  attentionInk:  '#FFFFFF',  // 4.97:1 on the fill, AA
  pool:         '#134B5D',   // pool water
  primary:      '#222120',
  primaryInk:   '#F4F4F2',
  decor:        '#4B4946',
  decorCool:    '#9A98B8',

  onPhoto:    '#FFFFFF',
  heroScrimTop:    'linear-gradient(to bottom, rgba(22,21,20,.62) 0%, rgba(22,21,20,.3) 22%, rgba(22,21,20,0) 42%)',
  heroScrimBottom: 'linear-gradient(to top, rgba(22,21,20,.78) 0%, rgba(22,21,20,.5) 40%, rgba(22,21,20,0) 78%)',
  cardShadow: '0 1px 2px rgba(34,33,32,.04), 0 10px 30px rgba(34,33,32,.06)',
  keyShadow:  '0 1px 3px rgba(34,33,32,.12), 0 4px 10px rgba(34,33,32,.05)',
  litShadow:  '0 2px 6px rgba(160,120,70,.22), 0 8px 20px rgba(201,169,127,.35)',
  shadow:   '0 30px 80px rgba(34,33,32,.16)',
  scrim:    'rgba(34,33,32,.42)',
  scale:    1,
};

// Dark separates layers by lightness, not shadow: shadows vanish on a
// near-black page.
export const DARK = {
  isDark:   true,
  paper:    '#121110',
  card:     '#222120',
  cardSoft: '#2E2C29',
  ink:      '#D6D2CB',   // softened plaster; full white smears at night
  inkSoft:  '#A8A49D',
  mute:     '#8F8B85',
  hair:     '#34322F',
  hairHi:   '#4A4743',

  lit:          '#C9A97F',
  litSoft:      'rgba(201,169,127,.18)',
  litFill:      '#C9A97F',
  litFillInk:   '#161514',
  litCell:      '#B8976B',
  keyFill:      '#2E2C29',
  selected:     '#D6D2CB',
  selectedSoft: 'rgba(214,210,203,.10)',
  selectedInk:  '#161514',
  busy:         '#D6D2CB',
  busySoft:     'rgba(214,210,203,.10)',
  live:         '#D6D2CB',
  heat:         '#D6D2CB',
  cool:         '#9A98B8',
  attention:    '#D08A5C',
  attentionFill: '#D08A5C',
  attentionInk:  '#161514',  // 6.49:1 on the fill
  pool:         '#5E9AAE',
  primary:      '#D6D2CB',
  primaryInk:   '#161514',
  decor:        '#A8A49D',
  decorCool:    '#9A98B8',

  onPhoto:    '#D6D2CB',
  heroScrimTop:    'linear-gradient(to bottom, rgba(18,17,16,.62) 0%, rgba(18,17,16,.3) 22%, rgba(18,17,16,0) 42%)',
  heroScrimBottom: 'linear-gradient(to top, rgba(18,17,16,.85) 0%, rgba(18,17,16,.45) 45%, rgba(18,17,16,0) 80%)',
  cardShadow: 'none',
  keyShadow:  'none',
  litShadow:  '0 0 22px 2px rgba(201,169,127,.22)',
  shadow:   '0 30px 80px rgba(0,0,0,.55)',
  scrim:    'rgba(0,0,0,.62)',
  scale:    1,
};
